import { sv } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'

import type {
    PaymentBillableConnectorsSummary,
    PaymentProductDisplayResponse,
    PaymentProductPriceDisplay,
} from '../../../common/redux/api/web-api'
import { convertPriceStringToNumber, formatToCurrencyString, SWEDISH_VAT } from '../../../common/services/utilsService'
import type {
    SubscriptionCardTag,
    SubscriptionMarketingFeature,
} from '../subscription-card-layout/subscription-card-layout'
import { LOCALE_CODE_MAP } from '../../../common/components/localization-provider'

type TextValues = Record<string, unknown>
type TextFn = (id: string, values?: TextValues) => string

export type BillingInterval = 'month' | 'year'

export type SubscriptionCardWidgetViewModel = {
    title: string
    description: string
    isRecommended: boolean
    tag?: SubscriptionCardTag
    discountInPercent?: number
    marketingFeatures: SubscriptionMarketingFeature[]
    primaryPrice: {
        amount: string
        suffix: string
    }
    detailLines: string[]
    footer: { type: 'cta'; isDisabled: boolean; text: string } | { type: 'message'; text: string }
}

const getOptionalText = (text: TextFn, key?: string) => {
    if (!key) return ''

    const value = text(key)
    return value === key ? '' : value
}

const pickMonthlyPrices = (prices: PaymentProductPriceDisplay[]) => {
    const monthlyPrices = prices.filter((p) => p.interval === 'month')
    const standard = monthlyPrices.find((p) => p.schedulePreview == null) ?? monthlyPrices[0]
    const promo = monthlyPrices.find((p) => p.schedulePreview != null)
    return { standard, promo }
}

const pickYearlyPrice = (prices: PaymentProductPriceDisplay[]) => prices.find((price) => price.interval === 'year')

const toVatPriceNumber = (unitPriceInCurrency: string | null | undefined) =>
    convertPriceStringToNumber(unitPriceInCurrency ?? '0') * SWEDISH_VAT

const computeYearlyDiscountPercent = (monthlyPriceNum: number, yearlyPriceNum: number) => {
    const yearlyTotalFromMonthly = monthlyPriceNum * 12
    if (!Number.isFinite(yearlyTotalFromMonthly) || yearlyTotalFromMonthly <= 0) return undefined
    if (!Number.isFinite(yearlyPriceNum) || yearlyPriceNum <= 0) return undefined
    const raw = (1 - yearlyPriceNum / yearlyTotalFromMonthly) * 100
    const rounded = Math.round(raw)
    return rounded > 0 ? rounded : undefined
}

export function buildSubscriptionCardWidgetViewModel({
    product,
    billableConnectors,
    marketingFeatures,
    billingInterval,
    text,
    locale,
    currency,
    now = new Date(),
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: {
    product: PaymentProductDisplayResponse
    billableConnectors?: PaymentBillableConnectorsSummary | null
    marketingFeatures: SubscriptionMarketingFeature[]
    billingInterval: BillingInterval
    text: TextFn
    locale: string
    currency: string
    now?: Date
    timeZone?: string
}): SubscriptionCardWidgetViewModel {
    const productName = product.name.trim().toLowerCase()
    const isFlex = productName === 'flex'
    const isPro = productName === 'pro'

    const filteredMarketingFeatures = marketingFeatures
        .filter((mf) => mf.label !== 'lblMarketingFeatureHistory')
        .map((mf) =>
            mf.label === 'lblBasicProductFeatureLine02' ? { ...mf, label: 'lblMarketingFeatureHistory' } : mf
        )

    const mappedMarketingFeatures = filteredMarketingFeatures.map((mf) => ({
        ...mf,
        isActive: (product.marketingFeatures ?? []).includes(mf.label) || (product.productLevel ?? 0) > mf.productLevel,
    }))

    const billableConnectorQuantity = billableConnectors?.quantities?.find(
        (quantity) => quantity.strategyKey === product.quantityStrategyKey
    )?.quantity
    const billableConnectorsCount = billableConnectorQuantity ?? 0

    const prices = product.prices ?? []
    const { standard: monthlyStandard, promo: monthlyPromo } = pickMonthlyPrices(prices)
    const yearlyItem = pickYearlyPrice(prices)
    const isMonthlyBilling = billingInterval === 'month'

    const monthlyPriceNum = toVatPriceNumber(monthlyStandard?.unitPriceInCurrency)
    const monthlyPromoPriceNum = toVatPriceNumber(monthlyPromo?.unitPriceInCurrency)
    const yearlyPriceNum = toVatPriceNumber(yearlyItem?.unitPriceInCurrency)

    const formatPrice = (price: number) => formatToCurrencyString(currency, price, locale)
    const monthlyPrice = formatPrice(monthlyPriceNum)
    const monthlyPromoPrice = formatPrice(monthlyPromoPriceNum)

    const monthlyRealtimePrice = formatPrice(monthlyPriceNum * billableConnectorsCount)
    const monthlyRealtimePromoPrice = formatPrice(monthlyPromoPriceNum * billableConnectorsCount)

    const yearlyPricePerMonth = formatPrice(yearlyPriceNum / 12)
    const yearlyRealtimePrice = formatPrice(yearlyPriceNum * billableConnectorsCount)
    const yearlyRealtimePricePerMonth = formatPrice((yearlyPriceNum / 12) * billableConnectorsCount)
    const yearlyRealTimeSaving =
        monthlyStandard && yearlyItem
            ? formatPrice(Math.max(0, monthlyPriceNum * 12 - yearlyPriceNum) * billableConnectorsCount)
            : null

    const discountPercent =
        isPro && !isMonthlyBilling ? computeYearlyDiscountPercent(monthlyPriceNum, yearlyPriceNum) : undefined

    const i18nDescriptionKey = isPro ? 'lblProduct1Description' : isFlex ? 'lblProduct2Description' : undefined
    const description = getOptionalText(text, i18nDescriptionKey)

    const tagKey = isPro ? 'lblSubscriptionTagPro' : isFlex ? 'lblSubscriptionTagFlex' : undefined
    const tagContent = getOptionalText(text, tagKey)
    const tag = tagContent
        ? {
              content: tagContent,
              className: isFlex ? 'bg-green-300' : undefined,
          }
        : undefined

    const isAvailableLater = new Date(product.availableFromDate) > now
    const availableFrom = isAvailableLater
        ? formatInTimeZone(product.availableFromDate, timeZone, 'yyyy-MM-dd HH:mm', {
              locale: LOCALE_CODE_MAP[locale as keyof typeof LOCALE_CODE_MAP] ?? sv,
          })
        : null

    const monthShort = text('lblMonthShort').toLowerCase()
    const perConnectorSuffix = `${text('lblPerConnector').toLowerCase()}/${monthShort}${isFlex ? '*' : ''}`

    const hasMonthlyPromo = isFlex && !!monthlyPromo?.unitPriceInCurrency

    const primaryAmount = isMonthlyBilling ? (hasMonthlyPromo ? monthlyPromoPrice : monthlyPrice) : yearlyPricePerMonth

    const hasBillableConnectors = billableConnectorQuantity != null && billableConnectorQuantity > 0

    const monthlyProDetailLines =
        isPro && hasBillableConnectors
            ? [
                  `${text('lblYourPrice')} ${monthlyRealtimePrice}/${monthShort}.`,
                  ...(yearlyRealTimeSaving ? [text('lblSaveYearly', { amount: yearlyRealTimeSaving })] : []),
              ]
            : []

    const monthlyFlexDetailLines = (() => {
        if (!isFlex) return []
        if (hasMonthlyPromo) {
            return [
                ...(hasBillableConnectors ? [`${text('lblYourPriceFirstMonth')} ${monthlyRealtimePromoPrice}.`] : []),
                `* ${text('lblProduct2PriceDescription', { amount: monthlyPrice }).toLowerCase()}`,
            ]
        }

        return hasBillableConnectors ? [`${text('lblYourPrice')} ${monthlyRealtimePrice}/${monthShort}.`] : []
    })()

    const yearlyProDetailLines =
        isPro && hasBillableConnectors && yearlyItem
            ? [
                  text('lblYourPriceAfterDiscount', {
                      amount: yearlyRealtimePrice,
                      interval: text('lblYear').toLowerCase(),
                      discountAmount: discountPercent ?? 0,
                  }),
                  `${text('lblCorrespondsTo')} ${yearlyRealtimePricePerMonth}/${monthShort}.`,
              ]
            : []

    const detailLines = (() => {
        if (!isMonthlyBilling) return yearlyProDetailLines
        if (isPro) return monthlyProDetailLines
        return monthlyFlexDetailLines
    })()

    const footer: SubscriptionCardWidgetViewModel['footer'] = isAvailableLater
        ? { type: 'message', text: text('lblThisProductWillBeAvailableFrom', { date: availableFrom }) }
        : {
              type: 'cta',
              text: text('lblChoosePlan'),
              isDisabled: !product.canBeBought || (isMonthlyBilling ? !monthlyStandard : !yearlyItem),
          }

    return {
        title: product.name,
        description,
        isRecommended: isPro,
        tag,
        discountInPercent: discountPercent,
        marketingFeatures: mappedMarketingFeatures,
        primaryPrice: {
            amount: primaryAmount,
            suffix: perConnectorSuffix,
        },
        detailLines,
        footer,
    }
}
